import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  ArrowLeft,
  Key,
  Globe,
  Copy,
  ShieldCheck,
  ShieldAlert,
  Download,
  Search,
  Plus,
  Trash2,
  Loader2,
  Save,
  Package,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Joomla 911 (AI Malware Scanner) is only relevant for licenses that
// actually include the AI Joomla Security suite on a paid tier.
const licenseSupportsJoomla911 = (lic) =>
  lic?.license_type !== 'free' && (lic?.elements || []).some((el) => (el || '').toLowerCase().includes('aijoomlasecurity'));

export default function LicenseManagementPage() {
  const { licenseId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [siteSearch, setSiteSearch] = useState('');
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [newSiteUrl, setNewSiteUrl] = useState('');
  const [savingSite, setSavingSite] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchLicense();
  }, [token, licenseId]);

  const fetchLicense = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await axios.get(`${API_URL}/api/portal/licenses/${licenseId}`, { headers });
      setLicense(res.data);
    } catch (error) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const copyKey = (key) => {
    if (!navigator.clipboard) {
      toast.error('Clipboard not available in this browser');
      return;
    }
    navigator.clipboard.writeText(key)
      .then(() => toast.success('Download key copied to clipboard'))
      .catch(() => toast.error('Could not copy — please copy the key manually'));
  };

  const handleDownloadExtension = async (element) => {
    try {
      const res = await axios.get(`${API_URL}/api/portal/licenses/${licenseId}/download`, {
        headers,
        params: { element },
      });
      if (res.data?.download_url) {
        window.open(res.data.download_url, '_blank');
        toast.success('Your download is starting...');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Download not available yet');
    }
  };

  const handleRegisterSite = async () => {
    if (!newSiteUrl.trim()) {
      toast.error('Please enter a site URL');
      return;
    }
    setSavingSite(true);
    try {
      await axios.post(
        `${API_URL}/api/portal/licenses/${licenseId}/sites`,
        { url: newSiteUrl.trim() },
        { headers }
      );
      toast.success('Site registered successfully');
      setNewSiteUrl('');
      setShowSiteModal(false);
      fetchLicense();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register site');
    } finally {
      setSavingSite(false);
    }
  };

  const handleRemoveSite = async (url) => {
    try {
      await axios.delete(`${API_URL}/api/portal/licenses/${licenseId}/sites`, {
        headers,
        params: { url },
      });
      toast.success('Site removed');
      fetchLicense();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove site');
    }
  };

  const handleToggleSiteJoomla911 = async (siteKey, enabled) => {
    try {
      await axios.patch(`${API_URL}/api/portal/licenses/${licenseId}/sites/${siteKey}/joomla911`, { enabled }, { headers });
      toast.success(enabled ? 'Joomla 911 enabled for this site' : 'Joomla 911 disabled for this site');
      fetchLicense();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update Joomla 911 access');
    }
  };

  const filteredSites = (license?.sites || []).filter((s) =>
    (s.url || '').toLowerCase().includes(siteSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 pt-[105px]" data-testid="license-management-loading">
        <Header />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-[#b9893d]" />
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !license) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 pt-[105px]" data-testid="license-management-not-found">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <Key className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">License not found</h2>
          <p className="text-slate-500 mt-2">This license doesn't exist or you don't have access to it.</p>
          <Button className="mt-6" onClick={() => navigate('/account')} data-testid="license-not-found-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to My Account
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 pt-[105px]" data-testid="license-management-page">
      <Header />

      {/* Page header */}
      <div className="bg-gradient-to-r from-[#1a1625] to-[#2d2438] text-white">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <button
            onClick={() => navigate('/account')}
            className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white mb-4 transition-colors"
            data-testid="license-manage-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back to My Account
          </button>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold" data-testid="license-manage-title">
              {license.primary_product_name || 'Joomla Extension'}
            </h1>
            {license.subscription_active ? (
              <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                <ShieldCheck className="w-3 h-3 mr-1" /> Updates Active
              </Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                <ShieldAlert className="w-3 h-3 mr-1" /> Updates Expired
              </Badge>
            )}
          </div>
          <p className="text-slate-300 mt-1">Manage registered sites, download keys &amp; Joomla 911 access for this license</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* License info + download */}
        <Card data-testid="license-manage-info-card">
          <CardContent className="p-6 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="capitalize">
                {license.unlimited_sites ? 'Multi-site' : 'Single-site'}
              </Badge>
              {license.is_bundle && <Badge variant="secondary">Bundle</Badge>}
              {license.expires_at && (
                <span className="text-xs text-slate-500">
                  Subscription {license.subscription_active ? 'renews/expires' : 'expired'} on{' '}
                  {new Date(license.expires_at).toLocaleDateString()}
                </span>
              )}
            </div>

            {!license.subscription_active && license.license_type !== 'free' && (
              <p className="text-xs text-amber-600" data-testid="license-manage-expired-note">
                Your update subscription has lapsed — you can still download your current version. Renew for the latest updates.
              </p>
            )}

            {(license.elements || []).length > 0 && (
              <div
                className="flex items-center justify-between bg-white border rounded-lg px-3 py-2"
                data-testid="license-manage-download-row"
              >
                <span className="flex items-center gap-2 text-sm text-slate-700 font-medium">
                  <Package className="w-4 h-4 text-slate-400" />
                  {license.primary_product_name || license.elements[0]}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownloadExtension(license.elements[0])}
                  data-testid="license-manage-download-btn"
                >
                  <Download className="w-4 h-4 mr-1.5" /> Download
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Per-site key explainer */}
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4" data-testid="license-manage-key-explainer">
          <Label className="text-xs text-blue-700 font-semibold flex items-center gap-1.5">
            <Key className="w-3.5 h-3.5" /> Per-site Download Keys (dlid)
          </Label>
          <p className="text-xs text-slate-600 mt-1.5">
            Each site you register gets its own unique download key, shown next to it below.
            A key only works on the exact site it was issued for — so one key can't be reused across multiple sites.
            Register your Joomla site first, then copy its key into the extension's "Download Key" field.
          </p>
        </div>

        {/* Registered Sites */}
        <Card data-testid="license-manage-sites-card">
          <CardHeader className="py-4 bg-slate-50 border-b border-slate-100 flex flex-row items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base font-semibold">Registered Sites</CardTitle>
              <CardDescription>
                {(license.sites || []).length} of {license.unlimited_sites ? 'unlimited' : (license.max_sites || 1)} site slot{license.max_sites === 1 ? '' : 's'} used.
              </CardDescription>
            </div>
            <Button
              size="sm"
              onClick={() => setShowSiteModal(true)}
              disabled={!license.unlimited_sites && (license.sites || []).length >= (license.max_sites || 1)}
              data-testid="license-manage-register-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Register Site
            </Button>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={siteSearch}
                onChange={(e) => setSiteSearch(e.target.value)}
                placeholder="Search by site URL..."
                className="pl-9"
                data-testid="license-manage-search"
              />
            </div>

            {filteredSites.length === 0 ? (
              <p className="text-sm text-slate-500 italic text-center py-12" data-testid="license-manage-no-sites">
                {siteSearch
                  ? 'No sites match your search.'
                  : 'No sites registered yet. Register your Joomla site URL to enable downloads & updates.'}
              </p>
            ) : (
              <div className="space-y-2">
                {filteredSites.map((s, idx) => (
                  <div
                    key={s.url + idx}
                    className="flex flex-wrap items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3"
                    data-testid={`license-manage-site-row-${idx}`}
                  >
                    <Globe className="w-4 h-4 text-slate-400 shrink-0" />
                    <span
                      className="text-sm font-medium text-slate-700 flex-1 truncate min-w-[140px]"
                      title={s.url}
                      data-testid={`license-manage-site-url-${idx}`}
                    >
                      {s.url}
                    </span>
                    <code
                      className="text-xs font-mono text-slate-700 bg-white border rounded px-2.5 py-1.5 max-w-[240px] truncate shrink-0"
                      title={s.key || license.key}
                      data-testid={`license-manage-site-key-${idx}`}
                    >
                      {s.key || license.key}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyKey(s.key || license.key)}
                      data-testid={`license-manage-copy-key-${idx}`}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    {licenseSupportsJoomla911(license) && (
                      <div className="flex items-center gap-1.5 shrink-0" data-testid={`license-manage-joomla911-row-${idx}`}>
                        <Switch
                          checked={s.joomla911_enabled !== false}
                          onCheckedChange={(checked) => handleToggleSiteJoomla911(s.key, checked)}
                          data-testid={`license-manage-joomla911-switch-${idx}`}
                        />
                        <span className="text-xs text-slate-500 whitespace-nowrap">Joomla 911</span>
                      </div>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50 shrink-0"
                      onClick={() => handleRemoveSite(s.url)}
                      data-testid={`license-manage-remove-site-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {!license.unlimited_sites && (license.sites || []).length >= (license.max_sites || 1) && (
              <p className="text-xs text-slate-500" data-testid="license-manage-limit-message">
                This license only allows {license.max_sites} site{license.max_sites === 1 ? '' : 's'}. Remove a site above to register a different one.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Register Site Dialog */}
      <Dialog open={showSiteModal} onOpenChange={setShowSiteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Register a Site</DialogTitle>
            <DialogDescription>
              Enter the URL of the Joomla site that will use this license for updates &amp; downloads.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="license-manage-site-url">Site URL</Label>
            <Input
              id="license-manage-site-url"
              placeholder="https://yourdomain.com"
              value={newSiteUrl}
              onChange={(e) => setNewSiteUrl(e.target.value)}
              data-testid="license-manage-site-url-input"
            />
            {!license.unlimited_sites && (
              <p className="text-xs text-slate-500">
                Single-site license: only {license.max_sites} site allowed.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSiteModal(false)}>Cancel</Button>
            <Button onClick={handleRegisterSite} disabled={savingSite} data-testid="license-manage-save-site-btn">
              {savingSite ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Register
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
